/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { Button, Form, FormGroup, Input, Label, FormFeedback, FormText } from 'reactstrap';
import { isValidEmail } from '@utils/index';
import { isPasswordValid } from '@utils/password';
import { RouteInterface } from '../../../types/routes';
import { generateUrl } from '../../../routes/util';
import { BaseStepCompProps } from '../types';
import { LoadingPlaceholder } from '@g17eco/molecules/loader-container';

const ReactPasswordStrength = React.lazy(() => import('@g17eco/molecules/react-password-strength'));

interface SignUpFormProps extends Pick<BaseStepCompProps, 'updateForm' | 'formData' | 'handleNext' | 'handlePrevious'> {
  handleCreateUser: () => void;
}

const termsAndConditionsRoute: RouteInterface = {
  id: 'terms_and_conditions',
  label: 'Terms of Use',
  path: '/legal-terms',
};
const privacyPolicyRoute: RouteInterface = {
  id: 'privacy_policy',
  label: 'Privacy Policy',
  path: '/legal-privacy-policy',
};

export const SignUpForm = (props: SignUpFormProps) => {
  const { updateForm, formData, handlePrevious, handleCreateUser } = props;

  const [isCheckboxChecked, setCheckbox] = useState(false);

  const passwordMatch = formData.password.length > 0 && formData.password === formData.rPassword;
  const isPassValid = passwordMatch && isPasswordValid(formData.password);
  const isEmailValid = isValidEmail(formData.email);
  const isNameValid = formData.firstName && formData.surname;
  const isFormValid = isNameValid && isEmailValid && isPassValid && isCheckboxChecked;

  return (
    <React.Suspense fallback={<LoadingPlaceholder height={630} />}>
    <section className='form-container'>
      <Form>
        <div className='row'>
          <div className='col'>
            <FormGroup>
              <Label for='firstName' >First name</Label>
              <Input
                placeholder='First name'
                valid={formData.firstName.length > 0}
                type='text'
                required
                name='firstName'
                defaultValue={formData.firstName}
                onChange={(e) => updateForm('firstName', e.target.value)}
              />
            </FormGroup>
          </div>
          <div className='col'>
            <FormGroup>
              <Label for='surname' >Last name</Label>
              <Input
                placeholder='Last name'
                valid={formData.surname.length > 0}
                type='text'
                required
                name='surname'
                defaultValue={formData.surname}
                onChange={(e) => updateForm('surname', e.target.value)}
              />
            </FormGroup>
          </div>
        </div>

        <div className='row mt-3'>
          <div className='col'>
            <FormGroup>
              <Label for='email'>E-mail</Label>
              <Input
                placeholder='Email'
                invalid={formData.email.length > 0 && !isEmailValid}
                valid={formData.email.length > 0 && isEmailValid}
                type='email'
                required
                defaultValue={formData.email}
                name='email'
                onChange={(e) => updateForm('email', e.target.value)}
              />
              <FormFeedback tooltip>Please ensure the e-mail is valid</FormFeedback>
            </FormGroup>
          </div>
        </div>

        <div className='row mt-3'>
          <div className='col'>
            <FormGroup>
              <Label for='password'>Password</Label>
              <ReactPasswordStrength
                style={{ width: '100%' }}
                required
                minLength={10}
                minScore={2}
                defaultValue={formData.password}
                changeCallback={(option) => updateForm('password', option.password)}
                inputProps={{ required: true, name: 'password', autoComplete: 'new-password', placeholder: 'Password' }}
              />
              <FormText>
                Min. 10 chars, lowercase/uppercase, number & special character
              </FormText>
            </FormGroup>
          </div>
        </div>

        <div className='row mt-3'>
          <div className='col'>
            <FormGroup>
              <Label for='rPassword'>Confirm password</Label>
              <ReactPasswordStrength
                style={{ width: '100%' }}
                required
                minLength={10}
                minScore={2}
                defaultValue={formData.rPassword}
                changeCallback={(option) => updateForm('rPassword', option.password)}
                inputProps={{ required: true, name: 'rPassword', autoComplete: 'new-password', placeholder: 'Confirm password' }}
                invalid={formData.rPassword.length > 0 && !passwordMatch}
              />
            </FormGroup>
          </div>
        </div>

        <div className='row mt-3'>
          <div className='col'>
            <FormGroup>
              <Label for='telephone'>Telephone number inc. international code</Label>
              <Input
                placeholder='e.g. (+65) 1 555 2222'
                type='text'
                defaultValue={formData.telephoneNumber}
                name='telephoneNumber'
                onChange={(e) => updateForm('telephoneNumber', e.target.value)}
              />
              <FormText>optional</FormText>
              <br />
              <FormText>We would love to support your reporting journey. Please provide your phone number if you would like us to call you about this.</FormText>
            </FormGroup>
          </div>
        </div>

        <div className='row mt-3'>
          <div className='col'>
            <FormGroup check>
              <Input type='checkbox' onChange={() => setCheckbox(!isCheckboxChecked)} checked={isCheckboxChecked} required className='checkbox' />
              <Label check onClick={() => setCheckbox(!isCheckboxChecked)}>
               <small> I have read and agree to the <a target='_blank' rel='noopener noreferrer' href={generateUrl(termsAndConditionsRoute)}>
                  {termsAndConditionsRoute.label}
                </a> and <a target='_blank' rel='noopener noreferrer' href={generateUrl(privacyPolicyRoute)}>
                  {privacyPolicyRoute.label}
                  </a>
                </small>
              </Label>
            </FormGroup>
          </div>
        </div>
      </Form>
      <div className='mt-5 d-flex'>
        {handlePrevious ? <Button color='link-secondary' onClick={handlePrevious}><i className='far fa-angle-left mr-2' />Back</Button> : null}
        <Button color='primary' className='ml-auto' onClick={handleCreateUser} disabled={!isFormValid} >Next<i className='far fa-angle-right ml-2' /></Button>
      </div>
    </section>
    </React.Suspense>
  );
}
